/**
 * Spot checkpoint thumbnails in R2 (same COVERS bucket as quest covers).
 */
import type { CoverExt } from "./quest-cover.ts";
import { COVER_CONTENT_TYPE } from "./quest-cover.ts";

/** Circle diameter on map marker sprites (quest-spot-map-layers), in CSS pixels. */
export const SPOT_THUMB_MARKER_PX = 40;

/** Stored marker thumbs should stay near this size (160×160 WebP). */
export const SPOT_THUMB_MAX_STORE_BYTES = 64 * 1024;

/** Encoded upload size: square WebP for marker thumbnails (2× {@link SPOT_THUMB_MARKER_PX} for retina). */
export const SPOT_THUMB_OUTPUT_PX = 160;

/** Raw upload ceiling before server-side normalize (full photos are rejected). */
export const SPOT_THUMB_MAX_RAW_UPLOAD_BYTES = 15 * 1024 * 1024;

export const SPOT_THUMB_ASPECT = 1;

const FILE = /^([0-9a-f]{32})\.(webp|jpg)$/;

/** R2 key: spot-thumbs/<missionId>/<seq>/<hash>.webp */
export function spotThumbnailKey(missionId: string, seq: number, hash: string, ext: CoverExt): string {
  return `spot-thumbs/${missionId}/${seq}/${hash}.${ext}`;
}

/** Staged upload before the quest save commits the checkpoint row. */
export function spotThumbnailPendingKey(missionId: string, seq: number, hash: string, ext: CoverExt): string {
  return `spot-thumbs-pending/${missionId}/${seq}/${hash}.${ext}`;
}

export function isSpotThumbnailPendingKey(key: string | null | undefined): boolean {
  return Boolean(key?.startsWith("spot-thumbs-pending/"));
}

export function spotThumbnailPendingUrl(missionId: string, seq: number, key: string | null | undefined): string | null {
  if (!key || !isSpotThumbnailPendingKey(key)) return null;
  const prefix = `spot-thumbs-pending/${missionId}/${seq}/`;
  if (!key.startsWith(prefix)) return null;
  const file = key.slice(prefix.length);
  if (!FILE.test(file)) return null;
  return `/api/spot-thumbs-pending/${encodeURIComponent(missionId)}/${seq}/${file}`;
}

/** Keep the same file hash when a checkpoint moves to a new seq (delete/reorder). */
export function reseqSpotThumbnailKey(key: string, missionId: string, seq: number): string {
  const parts = key.split("/");
  if (parts.length !== 4 || parts[0] !== "spot-thumbs" || parts[1] !== missionId) return key;
  const parsed = parseSpotThumbnailFile(parts[3]!);
  if (!parsed) return key;
  return spotThumbnailKey(missionId, seq, parsed.hash, parsed.ext);
}

export function spotThumbnailUrl(missionId: string, seq: number, key: string | null | undefined): string | null {
  if (!key) return null;
  const prefix = `spot-thumbs/${missionId}/${seq}/`;
  if (!key.startsWith(prefix)) return null;
  const file = key.slice(prefix.length);
  if (!FILE.test(file)) return null;
  return `/api/spot-thumbs/${encodeURIComponent(missionId)}/${seq}/${file}`;
}

/** Reverse {@link spotThumbnailUrl} for autosave bodies (R2 key or null). */
export function spotThumbnailKeyFromImageUrl(
  missionId: string,
  seq: number,
  url: string | null | undefined,
): string | null {
  if (!url?.trim()) return null;
  let path: string;
  if (url.startsWith("http")) {
    path = new URL(url).pathname;
  } else {
    path = url.split("?")[0] ?? url;
  }
  const prefix = `/api/spot-thumbs/${encodeURIComponent(missionId)}/${seq}/`;
  const pendingPrefix = `/api/spot-thumbs-pending/${encodeURIComponent(missionId)}/${seq}/`;
  if (path.startsWith(pendingPrefix)) {
    const file = path.slice(pendingPrefix.length);
    const parsed = parseSpotThumbnailFile(file);
    if (!parsed) return null;
    return spotThumbnailPendingKey(missionId, seq, parsed.hash, parsed.ext);
  }
  if (!path.startsWith(prefix)) return null;
  const file = path.slice(prefix.length);
  const parsed = parseSpotThumbnailFile(file);
  if (!parsed) return null;
  return spotThumbnailKey(missionId, seq, parsed.hash, parsed.ext);
}

export function parseSpotThumbnailFile(file: string): { hash: string; ext: CoverExt } | null {
  const m = FILE.exec(file);
  return m ? { hash: m[1]!, ext: m[2] as CoverExt } : null;
}

const CACHE_BUST_PARAM = "u";

/** Append a per-upload cache-bust query param (display URLs only; stripped when resolving R2 keys). */
export function withSpotThumbnailCacheBust(url: string, bust: number): string {
  if (!url || bust <= 0) return url;
  const base = url.split("?")[0] ?? url;
  const params = new URLSearchParams(url.includes("?") ? (url.split("?")[1] ?? "") : "");
  params.set(CACHE_BUST_PARAM, String(bust));
  return `${base}?${params.toString()}`;
}

export { COVER_CONTENT_TYPE as SPOT_THUMB_CONTENT_TYPE };
